import { BranchDef, LevelSim, Tower, branchViewKey } from "@highmarch/domain";
import { icon } from "./iconsKit";
import { tierFourArt } from "./portraits";

/**
 * The tower sheet's tier-4 lines (M21, docs/TOWERS_V2.md "Tier 4"): a tier-IV tower is shown by its branch — its name,
 * its tier-IV art (the branch's own icon when it ships, else the tier-3 one in the gold IV frame), its identity line,
 * and its two skills, learned or still to come on a skill card. A tier-3 tower whose type has a branch says that an
 * Upgrade card takes it to tier IV. Pure markup; ui/towerInfo.ts places it.
 */

export interface TierFourSheet {
  /** The sheet's title (the branch's name) and portrait, or null for a tower below tier IV. */
  readonly title: string | null;
  readonly portrait: string | null;
  /** The lines under the stats ("" for none), and a key that changes when they do (skills learned). */
  readonly html: string;
  readonly key: string;
}

/** The branch the tower's type took this run (tier IV or not), or null. */
export function runBranch(sim: LevelSim, t: Tower): BranchDef | null {
  const i = sim.ascension.branchOf[t.archetype] ?? -1;
  return i >= 0 ? sim.balance.branches.branches[i] : null;
}

export function tierFourSheet(sim: LevelSim, t: Tower, portraitPx: number): TierFourSheet {
  const b = runBranch(sim, t);
  if (b === null) return { title: null, portrait: null, html: "", key: "" };
  const learned = b.skills.map((s) => sim.ascension.learned[s.index]);
  const key = `${b.id}|${learned.join(",")}|${t.tier}`;
  if (t.tier < 4) {
    return { title: null, portrait: null, key, html: `<div class="dealt t4next">${icon("chev", 12)} An Upgrade card takes it to tier IV: <b>${b.name}</b></div>` };
  }
  const skills = b.skills.map((s, k) => `<li class="${learned[k] ? "on" : "off"}"><i class="disp">${k === 0 ? "A" : "B"}</i><span><b>${s.name}</b><small>${learned[k] ? s.description : "On a later skill card"}</small></span>${learned[k] ? icon("star", 12) : icon("lock", 12)}</li>`).join("");
  return {
    title: b.name,
    portrait: tierFourArt(b.towerId, b.id, portraitPx, "icon"),
    key,
    html: `<div class="t4sheet" data-view="${branchViewKey(b)}"><p class="t4id"><i class="ivplate disp">IV</i>${b.identity}</p><ul class="t4skills">${skills}</ul></div>`,
  };
}
